import React, { ReactNode } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';
import _get from 'lodash/get';

import { IconTrqkLogo } from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import { getWebpackEnvVar } from '@trqk/utils';

import { PATHS, FULL_PATHS } from '../../../constants';
import { TopNavItem } from './TopNavItem';
import './top-nav-layout.css';

const { GET_SESSION_IS_VALID, SIGN_OUT_WITH_COGNITO } = queryStrings;

interface Props {
    children: ReactNode;
    Footer?(): JSX.Element;
}

export const TopNavLayout = ({ children, Footer }: Props): JSX.Element => {
    const clientId = getWebpackEnvVar('web', 'AWS_APP_CLIENT_ID');
    const userPoolId = getWebpackEnvVar('web', 'AWS_USER_POOL_ID');
    const history = useHistory();
    const { data: sessionData } = useQuery(GET_SESSION_IS_VALID);
    const [signOutWithCognito] = useMutation(SIGN_OUT_WITH_COGNITO, {
        onCompleted: () => {
            // TODO handle errors here
            history.push(PATHS.LANDING);
        },
    });
    const sessionIsValid = _get(sessionData, ['session', 'sessionIsValid']);

    return (
        <div className="top-nav-layout h-screen bg-offwhite bg-no-repeat bg-left-top">
            <div className="bg-offwhite h-24 overflow-hidden fixed w-full top-0 z-10" />
            <div className="nav flex justify-between h-24 overflow-hidden fixed w-full top-0 z-20">
                <div className="pt-10 pl-10 h-full border-b border-solid border-tertiary-200">
                    <IconTrqkLogo opacity={1} fillColor="black" width={72} />
                </div>
                <span className="flex-auto pt-10 h-full border-b border-solid border-tertiary-200" />
                {sessionIsValid && (
                    <TopNavItem path={FULL_PATHS.STATEMENTS_CHARTS}>
                        <Link
                            to={({ search }) => ({
                                pathname: FULL_PATHS.STATEMENTS_CHARTS,
                                search,
                            })}
                        >
                            Statements
                        </Link>
                    </TopNavItem>
                )}
                {!sessionIsValid && (
                    <TopNavItem path={PATHS.STATEMENTS_PREVIEW}>
                        <Link to={PATHS.STATEMENTS_PREVIEW}>Statements</Link>
                    </TopNavItem>
                )}
                <TopNavItem disabled={!sessionIsValid} path={PATHS.WORKS}>
                    {sessionIsValid && <Link to={PATHS.WORKS}>Works</Link>}
                    {!sessionIsValid && <>Works</>}
                </TopNavItem>
                <TopNavItem path={PATHS.CONTACT}>
                    <a
                        href={PATHS.CONTACT}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Contact
                    </a>
                </TopNavItem>
                {sessionIsValid && (
                    <TopNavItem
                        path={PATHS.LANDING}
                        handleClick={(): void => {
                            signOutWithCognito({
                                variables: {
                                    clientId,
                                    userPoolId,
                                },
                            });
                        }}
                    >
                        Sign Out
                    </TopNavItem>
                )}
                {!sessionIsValid && (
                    <TopNavItem path={PATHS.SIGN_IN}>
                        <Link to={PATHS.SIGN_IN}>Sign In</Link>
                    </TopNavItem>
                )}
                <div className="absolute top-0 left-0">
                    <IconTrqkLogo width={639} />
                </div>
            </div>
            <div className="body-wrapper pt-24 h-full flex flex-col bg-no-repeat">
                <div className="flex-grow flex justify-center">{children}</div>
                {Footer && (
                    <div className="flex-none">
                        <Footer />
                    </div>
                )}
            </div>
        </div>
    );
};
